import * as React from 'react';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';

interface AttachmentPreviewProps {
  attachmentId: string;
  fileName: string;
  className?: string;
}

interface CachedPreview {
  url: string;
  expiresAt: number;
}

const previewCache = new Map<string, CachedPreview>();
const PREVIEW_CACHE_TTL_MS = 50 * 60 * 1000;

export function AttachmentPreview({ attachmentId, fileName, className }: AttachmentPreviewProps) {
  const { t } = useI18n();
  const [url, setUrl] = React.useState<string | null>(() => {
    const cached = previewCache.get(attachmentId);
    return cached && cached.expiresAt > Date.now() ? cached.url : null;
  });
  const [reloadAttempt, setReloadAttempt] = React.useState(0);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    const cached = previewCache.get(attachmentId);
    if (cached && cached.expiresAt > Date.now()) {
      setUrl(cached.url);
      setFailed(false);
      return () => { active = false; };
    }

    setUrl(null);
    setFailed(false);
    void api.get<{ data: { url: string } }>(`/attachments/${encodeURIComponent(attachmentId)}`, {
      params: { meta: true },
    }).then((response) => {
      if (!active) return;
      previewCache.set(attachmentId, {
        url: response.data.url,
        expiresAt: Date.now() + PREVIEW_CACHE_TTL_MS,
      });
      setUrl(response.data.url);
    }).catch(() => {
      if (active) setFailed(true);
    });

    return () => { active = false; };
  }, [attachmentId, reloadAttempt]);

  function handleImageError() {
    previewCache.delete(attachmentId);
    if (reloadAttempt === 0) {
      setUrl(null);
      setReloadAttempt(1);
    } else {
      setFailed(true);
    }
  }

  if (!url || failed) {
    return (
      <div className={`flex items-center justify-center rounded bg-surface-subtle text-xs text-ink-subtle ${className || ''}`}>
        {failed ? t('tp.attachment.previewUnavailable') : t('tp.attachment.loadingPreview')}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={fileName}
      className={className}
      loading="lazy"
      onError={handleImageError}
    />
  );
}
